import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IPC_CHANNEL } from "./_offline/electron.constants";
import { isElectron, electronApi } from "./_shared/environment-helper";
import { IpcEventChannel, IpcEventMap } from "./_offline/ipc-channels";

const {
  LANGUAGE: { GET_OS_LANGUAGE },
  UTILS: { CHECK_INTERNET_CONNECTION }
} = IPC_CHANNEL;

@Injectable({
  providedIn: "root"
})
export class ElectronService {
  public isElectron() {
    return isElectron();
  }

  private _checkElectronMode(): void | never {
    if (!this.isElectron()) {
      throw new Error("Not running in Electron");
    }
  }

  /**
   * Invokes an IPC event on the Electron main process with full type safety.
   *
   * This method ensures compile-time type checking between the `channel` name and its expected input/output types as defined in the @type {IpcEventMap}.
   * It is the **preferred alternative** to the non-typed `invoke` method because it prevents runtime type mismatches and improves maintainability.
   *
   * @template Channel - The IPC event channel key from `IpcEventMap`.
   *
   * @param {Channel} channel - The IPC event channel to invoke.
   * @param {IpcEventMap[Channel]["input"]} [parameters={}] - The input parameters for the specified channel.
   * @returns {IpcEventMap[Channel]["output"]} The expected output type for the invoked channel.
   *
   * @example
   * ```typescript
   * const result = await typedInvoke("LIST_FILES", { directory: "C:\\Users" });
   * // result is correctly inferred as IpcEventMap["LIST_FILES"]["output"]
   * ```
   */
  public typedInvoke<Channel extends IpcEventChannel>(
    channel: Channel,
    parameters: IpcEventMap[Channel]["input"] = {}
  ): IpcEventMap[Channel]["output"] {
    this._checkElectronMode();
    return electronApi.invoke(channel, parameters) as IpcEventMap[Channel]["output"];
  }

  /**
   * Invokes an IPC event on the Electron main process with optional type casting.
   *
   * @deprecated This function is deprecated. Use {@link typedInvoke} instead.
   */
  public invoke<T = any>(channel: string, ...args: any[]): Promise<T> {
    this._checkElectronMode();
    return electronApi.invoke(channel, ...args) as Promise<T>;
  }

  public sendCommand(channel: string, ...args: any[]): Observable<any> {
    return of(this.sendSyncCommand(channel, ...args));
  }

  public sendSyncCommand<T>(channel: string, ...args: any[]): any {
    this._checkElectronMode();
    return electronApi.sendSync(channel, ...args);
  }

  public sendCommandAsync(channel: string, ...args: any[]): Observable<any> {
    return of(this.sendAsyncCommand(channel, ...args));
  }

  public sendAsyncCommand<T>(channel: string, ...args: any[]): any {
    this._checkElectronMode();
    return electronApi.send(channel, ...args);
  }

  public onEvent(channel: string, listener: (event: any, ...args: any[]) => void): () => void {
    this._checkElectronMode();

    electronApi.onEvent(channel, listener);

    return () => {
      electronApi.offEvent(channel, listener);
    };
  }

  public offEvent(channel: string, callback: (event: any, ...args: any[]) => void): void {
    this._checkElectronMode();
    electronApi.offEvent(channel, callback);
  }

  public removeAllListeners(channel: string): void {
    this._checkElectronMode();
    electronApi.removeAllListeners(channel);
  }

  public async getOsLanguage(): Promise<string> {
    const language = await this.typedInvoke(GET_OS_LANGUAGE);
    return language;
  }

  async checkInternetConnectivity(): Promise<boolean> {
    if (!this.isElectron()) {
      return true;
    }

    try {
      const isOnline = await this.typedInvoke(CHECK_INTERNET_CONNECTION);
      return isOnline;
    } catch (error) {
      return false;
    }
  }
}
